import { useContext, useEffect, useState } from 'react';
import { Fade, Stack, Chip, Typography, Button, InputAdornment, Box, OutlinedInput } from '@mui/material';
import { SearchIcon } from 'lucide-react';
import { MetrcLogo, metrcTagsClasses, roomDetailClasses, tableClasses } from '../../utility';
import apiService from '../../../api/apiService';
import { FarmContext } from '../../../context/FarmContext';
import { FeedbackContext } from '../../../App';
import DisplayMetrcTagsTable from '../../utility/tables/DisplayMetrcTagsTable';

export default function _MetrcFacilityEmployeesTab({ currDarkThemeClass }) {
  const feedbackCX = useContext(FeedbackContext);
  const { farmContextValue } = useContext(FarmContext);

  const [farms, setFarms] = useState([]);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');

  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  useEffect(() => {
    if (farmContextValue && Array.isArray(farmContextValue.farms) && farmContextValue.farms.length > 0) {
      setFarms(farmContextValue.farms);

      const defaultFarm = farmContextValue.farms[0];
      setSelectedFarm(defaultFarm);
      setSelectedFilter(defaultFarm.farm_id);
    }
  }, [farmContextValue]);

  const employeeKeyTitleMap = [
    { key: 'employee_name', title: 'Employee Name' },
    { key: 'employee_license_number', title: 'License Number' },
    { key: 'employee_license_type', title: 'License Type' },
    { key: 'employee_is_industry_admin', title: 'Industry Admin' },
    { key: 'employee_is_owner', title: 'Owner' },
    { key: 'employee_is_manager', title: 'Manager' },
    { key: 'employee_start_date', title: 'Start Date' },
    { key: 'employee_end_date', title: 'End Date' },
  ];

  const formatData = items =>
    items.map((item, idx) => [
      `${idx + 1 + page * rowsPerPage}`.padStart(2, '0'),
      ...employeeKeyTitleMap.map(({ key }) => {
        const val = item[key];
        if (typeof val === 'boolean') return val ? 'True' : 'False';
        return val ?? 'N/A';
      }),
    ]);

  const fetchEmployees = async (farm = selectedFarm, currentPage = page, limit = rowsPerPage, search = searchTerm) => {
    if (!farm) return;

    setLoading(true);
    try {
      let url = `employee/?license_number=${encodeURIComponent(farm.license_number)}&limit=${limit}&page=${currentPage + 1}`;

      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await apiService.fetchInstance(url);
      if (response?.data) {
        const results = response.data.results || response.data || [];
        const formatted = formatData(results);
        setData(formatted);
        setTotalCount(response.data.count ?? results.length ?? 0);
      }
    } catch (error) {
      console.error('Error fetching employees', error);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Error fetching employees.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [selectedFarm, page, rowsPerPage, searchTerm]);

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const handleFilterChange = farmId => {
    const farmObj = farms.find(f => f.farm_id === farmId) || null;
    setSelectedFarm(farmObj);
    setSelectedFilter(farmId);
    setPage(0);
  };

  const sChipsProps = farm => ({
    key: farm.farm_id,
    label: farm.farm_name,
    variant: selectedFilter === farm.farm_id ? 'filled' : 'outlined',
    onClick: () => handleFilterChange(farm.farm_id),
    clickable: true,
    className: `${roomDetailClasses?.chip} ${currDarkThemeClass}`,
  });

  const metrcTagFarmChipsList = (
    <Stack direction="row" spacing={1}>
      {farms.length > 0 ? farms.map(farm => <Chip key={farm.farm_id} {...sChipsProps(farm)} />) : <Typography>No farms available</Typography>}
    </Stack>
  );

  const metrcTagFilterWrapperProps = {
    direction: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const metrcTagFilterProps = {
    direction: 'row',
    spacing: 3,
    alignItems: 'center',
  };

  const metrcTagFilterTypoProps = {
    className: `${metrcTagsClasses?.filterTitleTypo} ${currDarkThemeClass}`,
  };

  const handleSearch = event => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') event.preventDefault();
  };

  const outlinedInputProps = {
    className: `${roomDetailClasses.filterInput} ${currDarkThemeClass}`,
    size: 'small',
    placeholder: 'Search using Employee Name',
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon size={16} />
      </InputAdornment>
    ),
    onChange: handleSearch,
    onKeyPress: handleKeyPress,
    value: searchTerm,
  };

  const handleSyncToMetrc = async () => {
    if (!selectedFarm) return;
    setSyncLoading(true);

    const url = `employee/?license_number=${encodeURIComponent(selectedFarm.license_number)}&sync_to_metrc=true`;

    try {
      const response = await apiService.fetchInstance(url);
      if (response?.status === 200) {
        await fetchEmployees();
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Employees synced from Metrc successfully.',
        });
      }
    } catch (error) {
      console.error('Error syncing employees from Metrc', error);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Error syncing employees from Metrc.',
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const tableTitles = [{ title: 'Sl. No' }, ...employeeKeyTitleMap.map(e => ({ title: e.title }))];

  const displayProps = {
    animationClass: 'employeesTabContent',
    currDarkThemeClass,
    emptyTableMsg: 'No Employees Found',
    entity: 'Employees',
    tableEntries: data,
    tableTitles,
    toShowAddBtn: false,
    page,
    rowsPerPage,
    totalCount,
    onPageChange: handlePageChange,
    loading,
  };

  return (
    <Fade in={true} timeout={2000}>
      <Box>
        <Stack {...metrcTagFilterWrapperProps} padding={2}>
          <Stack {...metrcTagFilterProps} sx={{ flex: 1, minWidth: 0 }}>
            <Typography {...metrcTagFilterTypoProps} sx={{ whiteSpace: 'nowrap' }}>
              Show by Farm:
            </Typography>
            <Box
              sx={{
                flex: 1,
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  gap: 1,
                  minWidth: 'max-content',
                  pb: 0.5,
                }}
              >
                {metrcTagFarmChipsList}
              </Box>
            </Box>
          </Stack>

          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button
              onClick={handleSyncToMetrc}
              disabled={syncLoading || !selectedFarm}
              style={{ display: 'flex', alignItems: 'center', paddingRight: '20px' }}
              sx={{
                color: '#B1D23A',
                '&.Mui-disabled': { color: '#B1D23A' },
              }}
            >
              <MetrcLogo style={{ width: 24, height: 24, marginRight: 8 }} />
              {syncLoading ? 'Syncing...' : 'Sync From Metrc'}
            </Button>

            <OutlinedInput {...outlinedInputProps} />
          </Stack>
        </Stack>

        <Box className={`${tableClasses?.container} ${currDarkThemeClass}`}>
          <DisplayMetrcTagsTable {...displayProps} />
        </Box>
      </Box>
    </Fade>
  );
}
